/* eslint unused-imports/no-unused-imports: "off" */
import React, {useState} from "react"
import {breakpoints} from "@next/themes"
import {TextModel} from "models/Text"
import {Breadcrumbs} from "models/SearchState"
import {checkIfMerchandisedUrl} from "@next/utils"
import {getMoreLessButtonId} from "../../getMoreLessButtonId"
import SingleFacet from "../../../horizontalFilters/facetListBox/singleFacet"
import useSetFiltersOpenFromCookie from "../../../../hooks/useSetFiltersOpenFromCookie"
import HFClearAll from "../../../hfClearAll"
import HFMoreLessButton from "../../../hfMoreLessButton"
import {
    HFStyledHeader,
    StyledNavList,
    StyledMoreLessItem,
    StyledNav,
    FeatNav,
    StyledNavItem,
    StyledNavItemSecondRow,
    StyledNavListSecondRow,
    HFStyledClearAllContainer,
} from "./components"
import {HFStyledIcon} from "../../components"
import env from "../../../../config/env"
import {HORIZONTAL_FILTERS_ITEM_COUNT_DEFAULT} from "../../../../config/constants"
import FacetListBox from "../../../horizontalFilters/facetListBox"
import {FacetState} from "../../../../models/FacetsState"
import useHandleScroll from "../../useHandleScroll"
import {httpUrlTrimmer} from "../../../../utils/httpUrlTrimmer"
import useMediaQuery from "../../../../hooks/useMediaQuery"

export interface FiltersComponentProps {
    consolidatedFilters: string[]
    text: TextModel
    primaryFeatFacets: FacetState[]
    secondaryFeatFacets: FacetState[]
    handleSetFacet: (facetValue: string, filterName: string) => void
    isFilterSelected: boolean
    clearAllFilters: () => void
    url: string
    rememberFilters: boolean
    disableIcon: boolean
    breadcrumbs: Breadcrumbs
    totalRows: number
    datatestId?: string
}

export const dynamicFiltersCount = (primaryFeatFacets: FacetState[]) =>
    HORIZONTAL_FILTERS_ITEM_COUNT_DEFAULT - primaryFeatFacets.length

export const normaliseFilters = (consolidatedFilters: string[], primaryFeatFacets: FacetState[]) => {
    const consolidatedFiltersWithoutFeat = consolidatedFilters.filter(filter => filter !== "feat")
    const filtersCount = dynamicFiltersCount(primaryFeatFacets)
    const firstFilterList = consolidatedFiltersWithoutFeat.slice(0, filtersCount)
    const secondFilterList = consolidatedFiltersWithoutFeat.slice(filtersCount)
    return {firstFilterList, secondFilterList}
}

export const Cotton22FiltersComponent = ({
    consolidatedFilters,
    text,
    primaryFeatFacets,
    secondaryFeatFacets,
    handleSetFacet,
    isFilterSelected,
    clearAllFilters,
    url,
    rememberFilters,
    disableIcon,
    breadcrumbs,
    totalRows,
    datatestId,
}: FiltersComponentProps) => {
    const inPageFiltersVisible = useMediaQuery(`(max-width:${breakpoints.values.lg}px)`)

    useSetFiltersOpenFromCookie(url, rememberFilters)

    const [isMoreShown, setMoreShown] = useState(true)
    useHandleScroll(isMoreShown, setMoreShown)

    const toggleFiltersList = () => {
        setMoreShown(prevState => !prevState)
    }

    const {firstFilterList, secondFilterList} = normaliseFilters(consolidatedFilters, primaryFeatFacets)
    const showPriceAfterSecondaryFeat = secondaryFeatFacets?.length > 0 && secondFilterList.includes("Price")
    const isMerchandisedUrl = checkIfMerchandisedUrl(httpUrlTrimmer(url))

    const showMoreLessButton = secondFilterList.length > 0 || secondaryFeatFacets.length > 0
    const allTotalRows = totalRows || (showMoreLessButton ? totalRows + 1 : totalRows)

    const transitionedHandleFacet = (facetValue: string, filterName: string) => {
        handleSetFacet(facetValue, filterName)
    }

    return (
        <>
            <HFStyledHeader
                data-scrollanchor={!inPageFiltersVisible}
                id="plp-horizontal-filter-bar"
                data-testid={datatestId}
                breadcrumbsEnabled={breadcrumbs.enabled}
                isMerchandisedUrl={isMerchandisedUrl}
            >
                <StyledNav data-testid="plp-filter-cotton22-template">
                    <StyledNavList data-testid="horizontal-filter-first-row">
                        {primaryFeatFacets.map(featFacet => {
                            return (
                                <FeatNav key={featFacet.n}>
                                    <SingleFacet
                                        facet={featFacet}
                                        filterName="feat"
                                        handleSetFacet={transitionedHandleFacet}
                                        showCount
                                        shouldGetFocus={false}
                                        siteUrl=""
                                    />
                                </FeatNav>
                            )
                        })}
                        {firstFilterList.map(item => {
                            return (
                                <StyledNavItem key={item}>
                                    <FacetListBox key={item} filterName={item} facetBoxMinWidth={15} />
                                </StyledNavItem>
                            )
                        })}
                        {showMoreLessButton && (
                            <StyledMoreLessItem
                                onClick={toggleFiltersList}
                                data-testid={getMoreLessButtonId(isMoreShown)}
                                id={getMoreLessButtonId(isMoreShown)}
                            >
                                <HFMoreLessButton>
                                    {isMoreShown
                                        ? text.pages.filters.labels.moreButton
                                        : text.pages.filters.labels.lessButton}
                                </HFMoreLessButton>
                                {!disableIcon && (
                                    <HFStyledIcon data-testid="plp-horizontal-filter-more-less-icon">
                                        {isMoreShown ? (
                                            <img
                                                src={`${env.REACT_APP_BLOB_STORAGE_PATH}/icons/shared/horizontal-filters-plus.svg`}
                                                alt={text.pages.filters.iconAlt.moreIconAlt}
                                            />
                                        ) : (
                                            <img
                                                src={`${env.REACT_APP_BLOB_STORAGE_PATH}/icons/shared/horizontal-filters-minus.svg`}
                                                alt={text.pages.filters.iconAlt.lessIconAlt}
                                            />
                                        )}
                                    </HFStyledIcon>
                                )}
                            </StyledMoreLessItem>
                        )}
                    </StyledNavList>
                    {!isMoreShown && (
                        <StyledNavListSecondRow totalRows={allTotalRows} data-testid="horizontal-filter-second-row">
                            {secondFilterList.map(item => {
                                if (showPriceAfterSecondaryFeat && item === "Price") return null
                                return (
                                    <StyledNavItemSecondRow key={item}>
                                        <FacetListBox
                                            key={item}
                                            filterName={item}
                                            isOnSecondRow
                                            facetBoxMinWidth={15}
                                        />
                                    </StyledNavItemSecondRow>
                                )
                            })}
                            {secondaryFeatFacets.map(featFacet => {
                                return (
                                    <FeatNav key={featFacet.n}>
                                        <SingleFacet
                                            facet={featFacet}
                                            filterName="feat"
                                            handleSetFacet={transitionedHandleFacet}
                                            showCount
                                            shouldGetFocus={false}
                                            siteUrl=""
                                        />
                                    </FeatNav>
                                )
                            })}
                            {showPriceAfterSecondaryFeat && (
                                <StyledNavItemSecondRow data-testid="plp-filter-cotton22-2nd-row-price" key="Price">
                                    <FacetListBox key="Price" filterName="Price" isOnSecondRow facetBoxMinWidth={15} />
                                </StyledNavItemSecondRow>
                            )}
                        </StyledNavListSecondRow>
                    )}
                </StyledNav>
            </HFStyledHeader>
            <HFStyledClearAllContainer>
                {isFilterSelected && (
                    <HFClearAll childtestid="plp-horizontal-filter-clear-all" clearAllFilters={clearAllFilters}>
                        {text.buttons.clearAllFilters}
                    </HFClearAll>
                )}
            </HFStyledClearAllContainer>
        </>
    )
}

export default Cotton22FiltersComponent
